import React from "react";
import MissionLogStyles from "../../../styles/MissionLog.module.css";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    IconButton,
    ListItemText,
    Paper,
    Slide,
    Typography,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import {useUpload} from "../../../context/uploadContext";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const FileUpload = ({
                        onChooseFile,
                        onFileChange,
                        onUpload,
                        onCancel,
                        fileInputRef,
                        hasCloudStorage,
                        fileNameExist,
                        fullWidth = false,
                    }) => {
    const {uploadState, setUploadState} = useUpload();
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    const {
        file,
        uploading,
        uploadProgress,
        message,
        showPanel,
        failedUploads,
        uploadStatus,
    } = uploadState;
    const [panelVisible, setPanelVisible] = React.useState(showPanel);

    React.useEffect(() => {
        if (file) {
            setUploadState((prev) => ({...prev, uploadStatus: null}));
        }
    }, [file, setUploadState]);

    React.useEffect(() => {
        if (message) {
            setUploadState((prev) => ({...prev, uploadStatus: message}));
        }
    }, [message, setUploadState]);

    React.useEffect(() => {
        setPanelVisible(showPanel);
    }, [showPanel]);

    const handleClose = () => {
        setPanelVisible(false);
        onCancel();
    };

    const handleUploadClick = () => {
        if (fileNameExist) {
            setConfirmOpen(true);
        } else {
            onUpload();
        }
    };

    const handleConfirmUpload = () => {
        setConfirmOpen(false);
        onUpload();
    };

    const handleCancelUpload = () => {
        setConfirmOpen(false);
    };

    return (
        <div className={MissionLogStyles.fileUploadContainer}>
            <input type="file" hidden ref={fileInputRef} onChange={onFileChange}/>
            <Tooltip
                title={
                    !hasCloudStorage ? "Cloud storage not included in your current plan" : ""
                }
                arrow
            >
        <span style={fullWidth ? { display: 'block', width: '100%', height: '100%' } : {}}>
          <Button
              variant="outlined"
              onClick={onChooseFile}
              disabled={!hasCloudStorage || uploading}
              startIcon={<UploadFileIcon/>}
              fullWidth={fullWidth}
              sx={fullWidth ? { height: '100%', minHeight: 36.5 } : {}}
          >
            File Upload
          </Button>
        </span>
            </Tooltip>
            <Slide direction="up" in={panelVisible} mountOnEnter unmountOnExit>
                <Paper className={MissionLogStyles.uploadPanel}>
                    <Box className={MissionLogStyles.panelHeader}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {uploading ? (
                                <span className={MissionLogStyles.animatedText}>
                                    Uploading<span className={MissionLogStyles.dots}>...</span>
                                  </span>
                            ) : (
                                "Uploads"
                            )}
                        </Typography>
                        <IconButton size="small" onClick={handleClose}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Box>

                    {file && (
                        <Box mb={2}>
                            <Box className={MissionLogStyles.fileRow}>
                                <Box className={MissionLogStyles.fileInfo}>
                                    <InsertDriveFileIcon
                                        color="action"
                                        className={MissionLogStyles.fileIcon}
                                    />
                                    <ListItemText
                                        primary={file.name}
                                        primaryTypographyProps={{
                                            noWrap: true,
                                            className: MissionLogStyles.fileName,
                                        }}
                                    />
                                </Box>

                                {uploading && (
                                    <Box className={MissionLogStyles.progressWrapper}>
                                        <CircularProgress
                                            variant="determinate"
                                            value={uploadProgress}
                                            size={24}
                                            thickness={4}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}

                    <Collapse in={!!uploadStatus}>
                        <Alert
                            severity={uploadStatus === "success" ? "success" : "error"}
                            icon={
                                uploadStatus === "success" ? (
                                    <CheckCircleIcon fontSize="small"/>
                                ) : (
                                    <CancelIcon fontSize="small"/>
                                )
                            }
                            className={MissionLogStyles.statusAlert}
                        >
                            {uploadStatus === "success" ? "Upload completed" : "Upload failed"}
                        </Alert>
                    </Collapse>

                    {file && (
                        <Box className={MissionLogStyles.actionButtons}>
                            {uploadStatus === "interrupted" ? (
                                <>
                                    <Button variant="contained" size="small" onClick={handleUploadClick}>
                                        Resume Upload
                                    </Button>
                                    <Button variant="outlined" size="small" onClick={onCancel}>
                                        Cancel
                                    </Button>
                                </>
                            ) : (
                                !uploading &&
                                uploadStatus !== "success" && (
                                    <Button variant="contained" size="small" onClick={handleUploadClick}>
                                        Upload
                                    </Button>
                                )
                            )}
                        </Box>
                    )}
                </Paper>
            </Slide>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmOpen}
                onClose={handleCancelUpload}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">File Already Exists</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        A file with this name already exists. Do you want to overwrite it?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelUpload}>Cancel</Button>
                    <Button onClick={handleConfirmUpload} autoFocus>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default FileUpload;